import React, {useEffect, useState} from "react";
import {Modal, OverlayTrigger, Tooltip} from "react-bootstrap";
import {format} from 'date-fns';
import {Button} from "react-bootstrap";
import DatePicker from 'react-datepicker';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faExclamationCircle} from "@fortawesome/free-solid-svg-icons/faExclamationCircle";
import {useNavigate, useLocation} from "react-router-dom";
import {defaultFormState} from "../../../defaults/Forms";
import {contractDefault} from "../../../defaults/Items";
import {
  compareData,
  restoreFormData,
  onItemsListDeleteButtonClick,
  onCloseDetails, validateSelectField
} from "../../../helpers/ComponentHelper";

import AppComponentCardHeader from "../common/AppComponentCardHeader";
import LoadingDataDiv from "../common/LoadingDataDiv";
import DeleteWarningModal from "../common/DeleteWarningModal";
import TextInput from "../../elements/TextInput";
import TextArea from "../../elements/TextArea";
import useCrud from "../../../hooks/useCrud";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import {Table} from "../../table/Table";
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import Select from "../../elements/Select";
import '../../../css/react-datepicker.css';
import '../../../css/ContractForm.css';
import '../../../css/ResourceForms.css';
import '../../../css/ContractList.css';
import Toggler from "../../elements/Toggler";
import {ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {toastError, toastSuccess} from "../../../helpers/Toast";
import NumberInput from "../../elements/NumberInput";
import {isContractRichText, sanitizeContractRichHtml, unwrapContractRichText} from "../../../helpers/ContractRichText";

const SHOW_TEST_DATA_TOOLS = false;

const Contract = () => {
  const isDateValid = (dateString) => !isNaN(Date.parse(dateString));

  const dataUrl = "/contracts";
  const zusBasisUrl = "/zus-basis";
  const employeesUrl = "/employees";
  const businessPartnersUrl = "/business-partners";
  const hiringCompaniesUrl = "/business-partners/hiring-companies";
  const contractTypesUrl = "/contract-types";
  const workingTimesUrl = "/working-times";
  const positionsUrl = "/positions";
  const legalPersonsUrl = "/legal-persons";
  const relatedItemsUrl = null;
  const defaultItem = contractDefault;
  const itemName = "umowa";
  const itemNames = "umowy";
  const navigate = useNavigate();
  const location = useLocation();
  const {createItem, updateItem, deleteItem, getItems, getRelatedChildrenByParentId} = useCrud(dataUrl);
  const axiosPrivate = useAxiosPrivate();

  const [loading, setLoading] = useState(true);
  const [allowDelete, setAllowDelete] = useState(null);
  const [currentFormState, setCurrentFormState] = useState(defaultFormState);
  const [itemsList, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(defaultItem);
  const [backupItem, setBackupItem] = useState(defaultItem);
  const [itemChanged, setItemChanged] = useState(false);
  const [updateConflict, setUpdateConflict] = useState(false);
  const [conflictingRoles, setConflictingRoles] = useState(false);
  const [contractTypesList, setContractTypesList] = useState([]);
  const [filteredContractTypesList, setFilteredContractTypesList] = useState([]);
  const [dateFrom, setDateFrom] = useState(currentItem.dateFrom ? currentItem.dateFrom : format(new Date(), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(isDateValid(currentItem.dateTo) ? currentItem.dateTo : null);
  const [numberingPatternAvailable, setNumberingPatternAvailable] = useState(true);
  const [numberingPreview, setNumberingPreview] = useState(null);
  const [employeesList, setEmployeesList] = useState([]);
  const [businessPartnersList, setBusinessPartnersList] = useState([]);
  const [hiringCompaniesList, setHiringCompaniesList] = useState([]);
  const [legalPersonsList, setLegalPersonsList] = useState([]);
  const [zusBasisList, setZusBasisList] = useState([]);
  const [workingTimesList, setWorkingTimesList] = useState([]);
  const [positionsList, setPositionsList] = useState([]);
  const [contractTemplatesList, setContractTemplatesList] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [contractDocumentPreview, setContractDocumentPreview] = useState("");
  const [savedEmployeeDocumentPath, setSavedEmployeeDocumentPath] = useState("");
  const [employeeDocumentsRootPath, setEmployeeDocumentsRootPath] = useState("");
  const [savingContract, setSavingContract] = useState(false);
  const [bulkCreatingContracts, setBulkCreatingContracts] = useState(false);
  const [bulkDeletingContracts, setBulkDeletingContracts] = useState(false);
  const [itemLegalPerson, setItemLegalPerson] = useState(null);
  const billingTypeList = [{id: 1, name: "miesięczne", type: "MONTHLY"}, {id: 2, name: "godzinowe", type: "HOURLY"}];

  const LEGAL_PERSON_JURIDICAL = 1;
  const LEGAL_PERSON_NATURAL = 2;

  const columns = [
    {label: "Id", accessor: "id", sortable: true, searchable: false, visible: false},
    {label: "Numer umowy", accessor: "identifier", sortable: true, searchable: true},
    {label: "Opis", accessor: "notes", sortable: true, searchable: true},
    {label: "Data zawarcia", accessor: "dateFrom", sortable: true, searchable: true},
    {label: "Data zakończenia", accessor: "dateTo", sortable: true, searchable: true},
    {label: "Domyślna do grafiku", accessor: "defaultForScheduling", sortable: true, searchable: true, type: "bool"},
    {
      label: "Typ umowy",
      accessor: "contractType",
      sortable: true,
      searchable: true,
      type: "options",
      options: contractTypesList.map(type => ({
        value: type.name || type.shortName || "",
        label: type.shortName ? `${type.name} (${type.shortName})` : type.name
      })).filter(option => option.value),
      format: contractType => contractType ? `${contractType.shortName || contractType.name || ""}` : "",
      fieldsToInclude: ["name", "shortName"]
    },
    {
      label: "Wymiar etatu",
      accessor: "workingTime",
      sortable: true,
      searchable: true,
      format: workingTime => workingTime?.employmentPercentage != null
        ? `${Number(workingTime.employmentPercentage).toLocaleString("pl-PL", {maximumFractionDigits: 2})}%`
        : "",
      fieldsToInclude: ["name", "shortName", "employmentPercentage"]
    },
    {
      label: "Partner",
      accessor: "employee",
      sortable: true,
      searchable: true,
      format: employee => employee ? `${employee.firstName || ""} ${employee.lastName || ""}`.trim() : "",
      fieldsToInclude: ["firstName", "lastName"]
    },
    {label: "Edycja", accessor: "editBtn", sortable: false, searchable: false},
    {label: "Usuń", accessor: "deleteBtn", sortable: false, searchable: false}
  ];

  const state = {
    itemsList, setItems,
    currentItem, setCurrentItem,
    currentFormState, setCurrentFormState,
    defaultItem, backupItem, setBackupItem, itemChanged, setItemChanged,
    setAllowDelete,
    setRelatedItems: null
  }

  const onDelete = async () => {
    const response = await deleteItem(`${dataUrl}/${currentItem.id}`, currentItem.id, state);

    if (response === 401 || response === 403) {
      navigate('/login', {state: {from: location}, replace: true});
    }
  }


  const onSaveItemClick = async (e) => {
    e.preventDefault();

    const datesAreValid = (currentItem.dateFrom && dateRegex.test(currentItem.dateFrom)) &&
      (currentItem.dateTo && dateRegex.test(currentItem.dateTo)) &&
      (currentItem.dateFrom <= currentItem.dateTo);

    const legalPersonIsValid = validateSelectField(currentItem.legalPerson, "legalPerson");
    const contractTypeIsValid = validateSelectField(currentItem.contractType, "contractType");
    const businessPartnerIsValid = validateSelectField(currentItem.businessPartner, "businessPartner");
    const employeeIsValid = validateSelectField(currentItem.employee, "employee");
    const contractorIsValid = (businessPartnerIsValid || employeeIsValid);
    const billingType = currentItem.billingType?.type;
    const compensationIsValid = billingType === "HOURLY"
      ? currentItem.hourlyRate !== null && currentItem.hourlyRate !== undefined && currentItem.hourlyRate !== ""
      : billingType === "MONTHLY"
        ? currentItem.monthlyRate !== null && currentItem.monthlyRate !== undefined && currentItem.monthlyRate !== ""
        : false;
    const employmentContract = currentItem.contractType?.schedulePolicyType === "EMPLOYMENT";
    const workingTimeIsValid = !employmentContract || (currentItem.workingTime?.id > 0);
    const formIsValid = datesAreValid && legalPersonIsValid && contractTypeIsValid && contractorIsValid && compensationIsValid && workingTimeIsValid;

    if (!datesAreValid) toastError("Sprawdź daty obowiązywania umowy");
    if (!billingType) toastError("Wybierz formę rozliczenia");
    else if (!compensationIsValid) toastError(billingType === "HOURLY" ? "Podaj stawkę godzinową brutto" : "Podaj miesięczne wynagrodzenie brutto");
    if (!workingTimeIsValid) toastError("Wybierz wymiar czasu pracy dla umowy o pracę");
    if (!formIsValid) return;
    if (currentItem.autoNumbering && currentFormState.formAddingDataMode && !numberingPatternAvailable) {
      toastError("Brak wzorca numeracji dla wybranego typu umowy i roku");
      return;
    }

    let response;
    const item = {
      id: currentItem.id,
      docType: currentItem.docType,
      autoNumbering: currentItem.autoNumbering,
      identifier: currentItem.autoNumbering ? null : currentItem.identifier,
      dateFrom: currentItem.dateFrom,
      dateTo: currentItem.dateTo,
      conclusionPlace: currentItem.conclusionPlace || null,
      companyRepresentative: currentItem.companyRepresentative || null,
      positionName: currentItem.positionName || null,
      workPlace: currentItem.workPlace || null,
      employmentConditions: currentItem.employmentConditions || null,
      workStartDate: currentItem.workStartDate || null,
      notes: currentItem.notes,
      contractType: currentItem.contractType && currentItem.contractType.id > 0 ? {id: currentItem.contractType.id} : null,
      businessPartner: currentItem.businessPartner && currentItem.businessPartner.id > 0 ? {id: currentItem.businessPartner.id} : null,
      legalPerson: currentItem.legalPerson && currentItem.legalPerson.id > 0 ? {id: currentItem.legalPerson.id} : null,
      employee: currentItem.employee && currentItem.employee.id > 0 ? {id: currentItem.employee.id} : null,
      hiringCompany: currentItem.hiringCompany && currentItem.hiringCompany.id > 0 ? {id: currentItem.hiringCompany.id} : null,
      defaultForScheduling: !!currentItem.defaultForScheduling,
      status: currentItem.status,
      zusBasis: currentItem.zusBasis && currentItem.zusBasis.id > 0 ? {id: currentItem.zusBasis.id} : null,
      workingTime: employmentContract && currentItem.workingTime?.id > 0 ? {id: currentItem.workingTime.id} : null,
      billingType: currentItem.billingType?.type,
      hourlyRate: currentItem.hourlyRate,
      netHourlyRate: currentItem.netHourlyRate,
      monthlyRate: currentItem.monthlyRate,
      netMonthlyRate: currentItem.netMonthlyRate,
      plannedSeveranceCost: currentItem.plannedSeveranceCost === "" ? null : currentItem.plannedSeveranceCost,
      severanceNotes: currentItem.severanceNotes || null
    };


    try {
      setSavingContract(true);
      if (currentFormState.formAddingDataMode) {
        const {id, ...newContract} = item;
        response = await createItem(dataUrl, newContract, state);
      } else {
        response = await updateItem(`${dataUrl}/${item.id}`, item, state);
      }

      if (response.status === 401 || response.status === 403) {
        navigate('/login', {state: {from: location}, replace: true});
      } else if (response.status === 409) {
        setUpdateConflict(true);
      } else if (response.status >= 200 && response.status < 300) {
        const saved = {
          ...response.data,
          billingType: billingTypeList.find(b => b.type === response.data.billingType)
        };
        setCurrentItem(saved);
        setBackupItem(saved);
        setDateFrom(saved.dateFrom || dateFrom);
        setDateTo(saved.dateTo || null);
        setCurrentFormState(prev => ({...prev, formAddingDataMode: false, formSaveButtonDisabled: true}));
        setSavedEmployeeDocumentPath("");
        toastSuccess("Umowa została zapisana");
      } else {
        toastError(typeof response?.data === "string" ? response.data : response?.data?.message || "Nie udało się zapisać umowy");
      }
    } finally {
      setSavingContract(false);
    }
  }

  const dateRegex = /^(19|20)\d\d-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

  const checkRelatedItems = async (id) => {
    setAllowDelete(true);
  }

  const onClose = () => {
    setUpdateConflict(false);
    onCloseDetails({state});
  };


  useEffect(() => {
    if (updateConflict) setUpdateConflict(false);
    if (conflictingRoles) setConflictingRoles(false);
    if (!currentItem.legalPerson || currentItem.legalPerson.id === 0) {
      setCurrentItem(prev => ({...prev, contractType: null, businessPartner: null, employee: null}));
      setNumberingPatternAvailable(true);
      setCurrentFormState(prev => ({...prev, formSaveButtonDisabled: false}));
    }
    compareData(currentFormState, setCurrentFormState, currentItem, backupItem);
  }, [itemChanged]);


  useEffect(() => {
    if (allowDelete !== null) {
      onItemsListDeleteButtonClick(currentFormState, setCurrentFormState, itemName, allowDelete, currentItem.identifier);
    }
  }, [allowDelete]);


  useEffect(() => {
    const getData = async () => {
      const [itemsData, businessPartnersData, hiringCompaniesData, employeesData, contractTypesData, legalPersonsData, zusBasisData, workingTimesData, positionsData, contractTemplatesData, settingsData] =
        await Promise.all([
          getItems(dataUrl),
          getItems(businessPartnersUrl),
          getItems(hiringCompaniesUrl),
          getItems(employeesUrl),
          getItems(contractTypesUrl),
          getItems(legalPersonsUrl),
          getItems(zusBasisUrl),
          getItems(workingTimesUrl),
          getItems(positionsUrl),
          getItems("/contract-templates"),
          getItems("/settings")
        ]);

      const success =
        itemsData.status === 200 &&
        businessPartnersData.status === 200 &&
        hiringCompaniesData.status === 200 &&
        employeesData.status === 200 &&
        contractTypesData.status === 200 &&
        legalPersonsData.status === 200 &&
        zusBasisData.status === 200 &&
        workingTimesData.status === 200

      if (success) {
        setLoading(false);
        setItems(itemsData.data);
        setBusinessPartnersList(businessPartnersData.data);
        setHiringCompaniesList(hiringCompaniesData.data || []);
        setEmployeesList(employeesData.data);
        setContractTypesList(contractTypesData.data);
        setLegalPersonsList(legalPersonsData.data);
        setZusBasisList(zusBasisData.data);
        setWorkingTimesList(workingTimesData.data || []);
        setPositionsList(positionsData?.status === 200 ? positionsData.data || [] : []);
        setContractTemplatesList(contractTemplatesData?.status === 200 ? contractTemplatesData.data : []);
        setEmployeeDocumentsRootPath(settingsData?.status === 200 ? (settingsData.data?.employeeDocumentsRootPath || "") : "");
      } else {
        toastError("Nie udało się pobrać danych");
      }
    };
    getData();
  }, []);


  useEffect(() => {
    const fetchNumberingPreview = async () => {
      if (!currentFormState.showForm || !currentFormState.formAddingDataMode || !currentItem.autoNumbering) {
        setNumberingPatternAvailable(true);
        setNumberingPreview(null);
        return;
      }
      if (!currentItem.contractType?.id || !dateFrom) {
        setNumberingPreview(null);
        return;
      }
      try {
        const response = await axiosPrivate.get("/contracts/numbering-preview", {
          params: {contractTypeId: currentItem.contractType.id, dateFrom}
        });
        setNumberingPreview(response.data);
        setNumberingPatternAvailable(true);
        setCurrentFormState(prev => ({...prev, formSaveButtonDisabled: false}));
      } catch (e) {
        setNumberingPreview(null);
        if (e?.response?.status === 404) {
          setNumberingPatternAvailable(false);
          setCurrentFormState(prev => ({...prev, formSaveButtonDisabled: true}));
        } else if (e?.response?.status === 401 || e?.response?.status === 403) {
          navigate('/login', {state: {from: location}, replace: true});
        } else {
          toastError("Nie udało się pobrać podglądu numeracji umowy");
        }
      }
    };
    fetchNumberingPreview();
  }, [currentFormState.showForm, currentFormState.formAddingDataMode, currentItem.autoNumbering, currentItem.contractType?.id, dateFrom]);

  useEffect(() => {
    if (currentItem.legalPerson && currentItem.legalPerson.id) {
      const filteredList = contractTypesList.filter(ct => {
        if (currentItem.legalPerson.id === LEGAL_PERSON_JURIDICAL) {
          return ct.legalPersonJuridical;
        } else if (currentItem.legalPerson.id === LEGAL_PERSON_NATURAL) {
          return ct.legalPersonNatural;
        }
        return false;
      });
      setFilteredContractTypesList(filteredList);
    } else {
      setFilteredContractTypesList(contractTypesList);
    }
  }, [currentItem.legalPerson, contractTypesList]);


  const getCompleteItem = async (id) => {
    const [response, hiringCompaniesResponse] = await Promise.all([
      getItems(`${dataUrl}/${id}`),
      getItems(hiringCompaniesUrl)
    ]);
    if (hiringCompaniesResponse?.status === 200) {
      setHiringCompaniesList(hiringCompaniesResponse.data || []);
    }
    if (response.status === 200) {
      const item = {
        ...response.data,
        billingType: billingTypeList.find(b => b.type === response.data.billingType)
      };
      setCurrentItem(item);
      setDateFrom(item.dateFrom ? item.dateFrom : format(new Date(), 'yyyy-MM-dd'));
      setDateTo(isDateValid(item.dateTo) ? item.dateTo : null);
      setContractDocumentPreview("");
      setSelectedTemplateId("");
      setSavedEmployeeDocumentPath("");
      setCurrentFormState(prev => ({...prev, showForm: true, formAddingDataMode: false}));
      return item;
    } else if (response.status === 401 || response.status === 403) {
      navigate('/login', {state: {from: location}, replace: true})
    } else {
      toastError("Nie udało się pobrać danych.");
    }
  }


  const previewContractDocument = async () => {
    if (!currentItem.id) return;
    try {
      const response = await axiosPrivate.get(`/contracts/${currentItem.id}/document/preview`, {
        params: selectedTemplateId ? {templateId: selectedTemplateId} : {}
      });
      setContractDocumentPreview(response.data || "");
    } catch (e) {
      setContractDocumentPreview("");
      toastError(typeof e?.response?.data === "string" ? e.response.data : "Nie udało się wygenerować podglądu treści umowy");
    }
  };

  const generateContractDocument = async () => {
    if (!currentItem.id) return;
    try {
      const query = selectedTemplateId ? `?templateId=${selectedTemplateId}` : "";
      const response = await axiosPrivate.get(`/contracts/${currentItem.id}/document${query}`, {responseType: "blob"});
      const disposition = response.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
      const fileName = match ? decodeURIComponent(match[1].replace(/"/g, "")) : `umowa_${currentItem.identifier || currentItem.id}.docx`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toastError("Nie udało się wygenerować dokumentu umowy");
    }
  };


  const saveContractDocumentToEmployeeFolder = async () => {
    if (!currentItem.id || !currentItem.employee?.id) return;
    try {
      const response = await axiosPrivate.post(`/contracts/${currentItem.id}/document/save-to-employee-folder`, null, {
        params: selectedTemplateId ? {templateId: selectedTemplateId} : {}
      });
      setSavedEmployeeDocumentPath(response.data?.filePath || "");
      toastSuccess("DOCX zapisano w folderze pracownika");
    } catch (e) {
      toastError(typeof e?.response?.data === "string" ? e.response.data : e?.response?.data?.message || "Nie udało się zapisać DOCX w folderze pracownika");
    }
  };

  const createEmployeeContractsFor2026 = async () => {
    const confirmed = window.confirm(
      "Utworzyć testowe umowy na 2026 rok dla zatrudnionych pracowników bez żadnej umowy? ROLE_EMPLOYEE i ROLE_MINION będą rozliczane godzinowo, pozostali miesięcznie."
    );
    if (!confirmed) return;

    try {
      setBulkCreatingContracts(true);
      const response = await axiosPrivate.post("/contracts/bulk/employee-contracts-2026");
      const result = response.data || {};
      const created = Number(result.createdEmploymentContracts || 0) + Number(result.createdMandateContracts || 0);
      const skippedExisting = Number(result.skippedExistingContracts || 0);
      const skippedWithoutPosition = Number(result.skippedWithoutPosition || 0);
      toastSuccess(
        `Utworzono ${created} umów: ${result.createdEmploymentContracts || 0} o pracę i ${result.createdMandateContracts || 0} zlecenia. ` +
        `Pominięto ${skippedExisting} pracowników z istniejącą umową${skippedWithoutPosition ? ` oraz ${skippedWithoutPosition} bez stanowiska` : ""}.`
      );
      const refreshed = await getItems(dataUrl);
      if (refreshed?.status === 200) setItems(refreshed.data || []);
    } catch (e) {
      toastError(
        typeof e?.response?.data === "string"
          ? e.response.data
          : e?.response?.data?.message || "Nie udało się utworzyć umów pracowników"
      );
    } finally {
      setBulkCreatingContracts(false);
    }
  };

  const deleteEmployeeContractsFor2026 = async () => {
    const confirmed = window.confirm(
      "Usunąć wszystkie fikcyjne umowy utworzone przyciskiem „Utwórz umowy pracowników 2026”? Ręcznie utworzone umowy nie zostaną usunięte."
    );
    if (!confirmed) return;

    try {
      setBulkDeletingContracts(true);
      const response = await axiosPrivate.delete("/contracts/bulk/employee-contracts-2026");
      const result = response.data || {};
      const deleted = Number(result.deletedContracts || 0);
      toastSuccess(
        deleted > 0
          ? `Usunięto ${deleted} fikcyjnych umów dla ${result.affectedEmployees || 0} pracowników.`
          : "Nie znaleziono fikcyjnych umów utworzonych przez generator."
      );
      const refreshed = await getItems(dataUrl);
      if (refreshed?.status === 200) setItems(refreshed.data || []);
    } catch (e) {
      toastError(
        typeof e?.response?.data === "string"
          ? e.response.data
          : e?.response?.data?.message || "Nie udało się usunąć fikcyjnych umów pracowników"
      );
    } finally {
      setBulkDeletingContracts(false);
    }
  };

  const resetForm = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    setNumberingPreview(null);
    setSelectedTemplateId("");
    setContractDocumentPreview("");
    setSavedEmployeeDocumentPath("");
    setCurrentItem({...defaultItem, dateFrom: today, dateTo: null, workStartDate: today});
    setDateFrom(today);
    setDateTo(null);
    setCurrentFormState(prev => ({...prev, formAddingDataMode: true, showForm: true}));
  }


  const addDataButtonProps = {
    setCurrentItem,
    setBackupItem,
    defaultItem: {
      ...defaultItem,
      dateFrom: format(new Date(), 'yyyy-MM-dd'),
      dateTo: null,
      workStartDate: format(new Date(), 'yyyy-MM-dd')
    },
    currentFormState,
    setCurrentFormState,
    formDescription: null,
    formHeader: `Nowy element`,
    buttonTitle: `Dodaj nową umowę`
  }

  let dataSectionContent;
  if (loading) {
    dataSectionContent = <LoadingDataDiv/>
  } else if (itemsList.length > 0) {
    dataSectionContent =
      <Table
        columns={columns}
        state={state}
        tableClassName="contract-table"
        tableContainerClassName="contract-table-scroll"
        checkRelatedItems={checkRelatedItems}
        formHeader={`Edycja ${itemName}`}
        relatedItemsUrl={relatedItemsUrl}
        getCompleteItem={getCompleteItem}
        exportFileName="umowy.xlsx"
      />
  } else {
    dataSectionContent =
      <div className={"warn_info"}><FontAwesomeIcon
        className={"warning-icon div-visible"} icon={faExclamationCircle}/>&nbsp;&nbsp;&nbsp;NIE ZNALEZIONO ŻADNYCH
        DANYCH
      </div>
  }


  return (
    <div id="layoutSidenav_content" className="dictionary-page resource-page">
      <ToastContainer/>
      {currentFormState.showForm ? (
        <AppComponentCardHeader
          title={`Edycja ${itemNames.charAt(0).toUpperCase()}${itemNames.slice(1).toLowerCase()} -> ${currentItem.identifier}`}/>
      ) : (
        <AppComponentCardHeader
          title={`Zawarte umowy`}
          buttonProps={addDataButtonProps}
          onAddButtonClick={resetForm}
        />
      )}

      <div className="container-fluid px-4 py-3">

        {!currentFormState.showForm && (
          <>
            {SHOW_TEST_DATA_TOOLS && (
              <div className="card shadow-sm mb-3 p-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div>
                    <strong>Automatyczne umowy pracowników – 2026</strong>
                    <div className="small text-muted">
                      Tworzy testowe umowy dla zatrudnionych pracowników bez umowy. ROLE_EMPLOYEE i ROLE_MINION otrzymują rozliczenie godzinowe, pozostali miesięczne. Stawki są wyliczane względem średniego wynagrodzenia zapisanego w stawkach ZUS.
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline-primary"
                      disabled={bulkCreatingContracts || bulkDeletingContracts}
                      onClick={createEmployeeContractsFor2026}
                    >
                      {bulkCreatingContracts ? "Tworzenie umów..." : "Utwórz umowy pracowników 2026"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline-danger"
                      disabled={bulkCreatingContracts || bulkDeletingContracts}
                      onClick={deleteEmployeeContractsFor2026}
                    >
                      {bulkDeletingContracts ? "Usuwanie umów..." : "Usuń fikcyjne umowy 2026"}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <div className="card shadow-sm mb-5 resource-list-card contract-list-card">
              {dataSectionContent}
            </div>
          </>
        )}

        {currentFormState.showForm && (
          <Modal
            show={true}
            onHide={onClose}
            backdrop="static"
            keyboard={false}
            size="xl"
            centered
            dialogClassName="contract-edit-modal"
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {currentFormState.formAddingDataMode ? "Nowa umowa" : `Edycja umowy: ${currentItem.identifier || currentItem.id}`}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="contract-form-topbar">
                <div className="data_changed" id="data-changed">
                  <FontAwesomeIcon icon={faExclamationCircle}/>&nbsp;{currentFormState.formDataChangedWarning}
                </div>
                {!currentFormState.formAddingDataMode && (
                  <Button variant="outline-secondary" size="sm" id="btn-restore" className="btn-restore" onClick={() => {
                    setUpdateConflict(false);
                    restoreFormData({state});
                  }}>
                    Przywróć wartości
                  </Button>
                )}
              </div>

              <form id="add-contract-form" name="add-contract-form">
                <div className="contract-form-section">
                  <div className="contract-form-section-title">Strony i rodzaj umowy</div>
                  <div className="row g-3">
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label" htmlFor="legalPerson"><span className="required">*</span> Forma prawna</label>
                      <Select
                        label="legalPerson"
                        propertyName="legalPersonId"
                        required={false}
                        state={state}
                        itemsList={legalPersonsList}
                        itemName="legalPerson"
                        displayNames={["name"]}
                      />
                    </div>

                    <div className="col-lg-4 col-md-6">
                      <label className="form-label" htmlFor="contractType"><span className="required">*</span> Typ umowy</label>
                      {(!currentItem.legalPerson || currentItem.legalPerson.id === 0) ? (
                        <div className="form-control contract-form-placeholder">Najpierw wybierz formę prawną</div>
                      ) : (
                        <Select
                          label="contractType"
                          propertyName="contractTypeId"
                          required={true}
                          state={state}
                          itemsList={filteredContractTypesList}
                          itemName="contractType"
                          displayNames={["name"]}
                          disabled={!currentFormState.formAddingDataMode && currentItem.autoNumbering}
                        />
                      )}
                    </div>

                    {currentItem.contractType?.schedulePolicyType === "EMPLOYMENT" && (
                      <div className="col-lg-4 col-md-6">
                        <label className="form-label" htmlFor="workingTime"><span className="required">*</span> Wymiar czasu pracy</label>
                        {workingTimesList.length > 0 ? (
                          <>
                            <Select
                              label="workingTime"
                              propertyName="workingTimeId"
                              required={true}
                              state={state}
                              itemsList={workingTimesList}
                              itemName="workingTime"
                              displayNames={["name", "shortName"]}
                            />
                            <div className="small text-muted mt-1">Wpływa na normę godzin i koszt godzinowy wynagrodzenia miesięcznego.</div>
                          </>
                        ) : (
                          <div className="err_info">
                            Brak zdefiniowanych wymiarów etatu. <a className="app-link" href="#" onClick={event => { event.preventDefault(); navigate("/app/working-time"); }}>Dodaj wymiar</a>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="col-lg-4 col-md-6">
                      <label className="form-label" htmlFor="zusBasis"><span className="required">*</span> Typ podstawy ZUS</label>
                      {(!currentItem.legalPerson || currentItem.legalPerson.id === 0) ? (
                        <div className="form-control contract-form-placeholder">Najpierw wybierz formę prawną</div>
                      ) : (
                        <Select
                          label="zusBasis"
                          propertyName="zusBasisId"
                          required={true}
                          state={state}
                          itemsList={zusBasisList}
                          itemName="zusBasis"
                          displayNames={["name"]}
                          tooltipProperty="description"
                        />
                      )}
                    </div>

                    <div className="col-lg-4 col-md-6">
                      <label className="form-label" htmlFor="businessPartner"><span className="required">*</span> Wykonawca</label>
                      {(!currentItem.legalPerson || currentItem.legalPerson.id === 0) ? (
                        <div className="form-control contract-form-placeholder">Najpierw wybierz formę prawną</div>
                      ) : currentItem.legalPerson.id === LEGAL_PERSON_JURIDICAL ? (
                        <Select
                          label="businessPartner"
                          propertyName="businessPartnerId"
                          required={true}
                          state={state}
                          itemsList={businessPartnersList}
                          itemName="businessPartner"
                          displayNames={["shortName"]}
                          disabled={!numberingPatternAvailable}
                        />
                      ) : (
                        <Select
                          label="employee"
                          propertyName="employeeId"
                          required={false}
                          state={state}
                          itemsList={employeesList}
                          itemName="employee"
                          displayNames={["firstName", "lastName"]}
                          disabled={!numberingPatternAvailable}
                        />
                      )}
                    </div>

                    {currentItem.employee?.id > 0 && (
                      <div className="col-lg-4 col-md-6">
                        <label className="form-label" htmlFor="hiringCompany">Firma zatrudniająca / agencja</label>
                        <Select
                          label="hiringCompany"
                          propertyName="hiringCompanyId"
                          required={false}
                          state={state}
                          itemsList={hiringCompaniesList}
                          itemName="hiringCompany"
                          displayNames={["shortName"]}
                        />
                      </div>
                    )}

                    {currentItem.employee?.id > 0 && (
                      <div className="col-lg-4 col-md-6 d-flex align-items-end">
                        <div className="contract-inline-toggle">
                          <Toggler propertyName="defaultForScheduling" state={state} disabled={false}/>
                          <div>
                            <div className="fw-semibold">Domyślna do grafiku i kosztów</div>
                            <div className="small text-muted">Używana domyślnie przy kilku aktywnych umowach.</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="contract-form-section">
                  <div className="contract-form-section-title">Dane do treści umowy</div>
                  <div className="row g-3">
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label" htmlFor="conclusionPlace">Miejscowość zawarcia umowy</label>
                      <TextInput propertyName="conclusionPlace" required={false} state={state}/>
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label" htmlFor="companyRepresentative">Reprezentant firmy</label>
                      <TextInput propertyName="companyRepresentative" required={false} state={state}/>
                      <div className="small text-muted mt-1">Jeżeli pole pozostanie puste, dokument użyje reprezentanta z danych firmy.</div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label" htmlFor="positionName">Stanowisko</label>
                      <input
                        id="positionName"
                        className="form-control"
                        list="contract-position-options"
                        value={currentItem.positionName || ""}
                        onChange={event => {
                          setCurrentItem({...currentItem, positionName: event.target.value});
                          setItemChanged(!itemChanged);
                        }}
                      />
                      <datalist id="contract-position-options">
                        {positionsList.map(position => <option key={position.id} value={position.name || position.shortName || ""}/>) }
                      </datalist>
                    </div>
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label" htmlFor="workPlace">Miejsce wykonywania pracy</label>
                      <TextInput propertyName="workPlace" required={false} state={state}/>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <label className="form-label" htmlFor="workStartDate">Data rozpoczęcia pracy</label>
                      <DatePicker
                        className="form-control"
                        selected={currentItem.workStartDate ? new Date(currentItem.workStartDate) : null}
                        onChange={date => {
                          const formattedDate = date ? format(date, 'yyyy-MM-dd') : null;
                          setCurrentItem({...currentItem, workStartDate: formattedDate});
                          setItemChanged(!itemChanged);
                        }}
                        dateFormat="yyyy-MM-dd"
                        isClearable
                      />
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <label className="form-label">Wymiar czasu pracy</label>
                      <div className="form-control contract-form-placeholder">
                        {currentItem.workingTime?.name || currentItem.workingTime?.shortName || "Nie dotyczy / nie wybrano"}
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label" htmlFor="employmentConditions">Warunki zatrudnienia</label>
                      <TextArea propertyName="employmentConditions" required={false} rows="3" state={state}/>
                    </div>
                  </div>
                </div>

                <div className="contract-form-section">
                  <div className="contract-form-section-title">Numeracja i okres obowiązywania</div>
                  <div className="row g-3">
                    <div className="col-lg-3 col-md-6">
                      <label className="form-label">Numeracja</label>
                      <div className="contract-inline-toggle contract-control-height">
                        {currentFormState.formAddingDataMode ? (
                          <Toggler propertyName="autoNumbering" state={state} disabled={false}/>
                        ) : (
                          <span className={currentItem.autoNumbering ? "ok_info" : "err_info"}>{currentItem.autoNumbering ? "SYSTEMOWA" : "RĘCZNA"}</span>
                        )}
                        {currentFormState.formAddingDataMode && <span>Użyj systemowej numeracji</span>}
                      </div>
                    </div>

                    <div className="col-lg-5 col-md-6">
                      <label className="form-label" htmlFor="identifier">Numer / identyfikator umowy</label>
                      {!currentFormState.formAddingDataMode && currentItem.autoNumbering ? (
                        <TextInput propertyName="identifier" value={currentItem.identifier} required={true} state={state} disabled={true}/>
                      ) : !currentItem.autoNumbering ? (
                        <TextInput propertyName="identifier" required={true} state={state} disabled={false} prefix="U_"/>
                      ) : (
                        <input className="form-control" value={numberingPreview?.identifier || ""} readOnly placeholder="Numer zostanie nadany przy zapisie"/>
                      )}
                      {currentFormState.formAddingDataMode && currentItem.autoNumbering && numberingPreview && (
                        <div className="small text-muted mt-1">
                          Wzorzec: <strong>{numberingPreview.pattern}</strong> · kolejny numer: <strong>{numberingPreview.nextNumber}</strong>
                        </div>
                      )}
                      <div className={!numberingPatternAvailable ? "err_info mt-1" : "offscreen"}>
                        <FontAwesomeIcon icon={faInfoCircle}/>&nbsp;
                        Brak wzorca numeracji dla wybranego typu umowy i roku. <a className="app-link" href="#" onClick={e => { e.preventDefault(); navigate('/app/document-numbering-pattern'); }}>Dodaj wzorzec</a>
                      </div>
                    </div>

                    <div className="col-lg-2 col-md-6">
                      <label className="form-label" htmlFor="dateFrom"><span className="required">*</span> Data zawarcia</label>
                      <DatePicker
                        className="form-control"
                        selected={currentItem.dateFrom ? new Date(currentItem.dateFrom) : new Date(dateFrom)}
                        onChange={date => {
                          if (!date) return;
                          const formattedDate = format(date, 'yyyy-MM-dd');
                          setDateFrom(formattedDate);
                          setItemChanged(!itemChanged);
                          setCurrentItem({...currentItem, dateFrom: formattedDate});
                          setCurrentFormState(prev => ({...prev, formSaveButtonDisabled: false}));
                        }}
                        dateFormat="yyyy-MM-dd"
                        disabled={!currentFormState.formAddingDataMode && currentItem.autoNumbering}
                      />
                    </div>

                    <div className="col-lg-2 col-md-6">
                      <label className="form-label" htmlFor="dateTo"><span className="required">*</span> Data zakończenia</label>
                      <DatePicker
                        className="form-control"
                        selected={currentItem.dateTo ? new Date(currentItem.dateTo) : null}
                        onChange={date => {
                          if (!date) return;
                          const formattedDate = format(date, 'yyyy-MM-dd');
                          setDateTo(formattedDate);
                          setItemChanged(!itemChanged);
                          setCurrentItem({...currentItem, dateTo: formattedDate});
                          setCurrentFormState(prev => ({...prev, formSaveButtonDisabled: false}));
                        }}
                        dateFormat="yyyy-MM-dd"
                      />
                    </div>
                  </div>
                </div>

                <div className="contract-form-section contract-compensation-section">
                  <div className="contract-form-section-title">Wynagrodzenie</div>
                  <div className="row g-3 align-items-end">
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label" htmlFor="billingType"><span className="required">*</span> Forma rozliczenia</label>
                      <Select
                        label="billingType"
                        propertyName="billingType"
                        required={true}
                        state={state}
                        itemsList={billingTypeList}
                        itemName="billingType"
                        displayNames={["name"]}
                      />
                    </div>

                    {currentItem.billingType?.type === "HOURLY" && (
                      <>
                        <div className="col-lg-4 col-md-6">
                          <label className="form-label" htmlFor="hourlyRate"><span className="required">*</span> Stawka godzinowa brutto</label>
                          <NumberInput propertyName="hourlyRate" defaultValue="0" min="0" max="99999" required={true} state={state}/>
                        </div>
                        <div className="col-lg-4 col-md-6">
                          <label className="form-label" htmlFor="netHourlyRate">Stawka godzinowa netto</label>
                          <NumberInput propertyName="netHourlyRate" defaultValue="" min="0" max="99999" required={false} state={state}/>
                        </div>
                      </>
                    )}

                    {currentItem.billingType?.type === "MONTHLY" && (
                      <>
                        <div className="col-lg-4 col-md-6">
                          <label className="form-label" htmlFor="monthlyRate"><span className="required">*</span> Wynagrodzenie miesięczne brutto</label>
                          <NumberInput propertyName="monthlyRate" defaultValue="0" min="0" max="999999" required={true} state={state}/>
                        </div>
                        <div className="col-lg-4 col-md-6">
                          <label className="form-label" htmlFor="netMonthlyRate">Wynagrodzenie miesięczne netto</label>
                          <NumberInput propertyName="netMonthlyRate" defaultValue="" min="0" max="999999" required={false} state={state}/>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {!currentFormState.formAddingDataMode && (
                  <div className="contract-form-section">
                    <div className="contract-form-section-title">Dokument DOCX</div>
                    <div className="row g-3 align-items-end">
                      <div className="col-lg-5">
                        <label className="form-label" htmlFor="contractTemplate">Wzór treści umowy</label>
                        <select id="contractTemplate" className="form-select" value={selectedTemplateId} onChange={e => {
                          setSelectedTemplateId(e.target.value);
                          setContractDocumentPreview("");
                          setSavedEmployeeDocumentPath("");
                        }}>
                          <option value="">Automatyczny wzór dla typu umowy</option>
                          {contractTemplatesList
                            .filter(template => !template.contractTypeId || template.contractTypeId === currentItem.contractType?.id)
                            .map(template => <option key={template.id} value={template.id}>{template.name}</option>)}
                        </select>
                      </div>
                      <div className="col-lg-7">
                        <div className="d-flex flex-wrap gap-2">
                          <Button type="button" variant="outline-secondary" onClick={previewContractDocument}>Podgląd treści</Button>
                          <Button type="button" variant="outline-primary" onClick={generateContractDocument}>Pobierz DOCX</Button>
                          {currentItem.employee?.id > 0 && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id="employee-folder-tooltip">
                                  {employeeDocumentsRootPath?.trim()
                                    ? "Zapisz dokument w katalogu pracownika skonfigurowanym w ustawieniach aplikacji."
                                    : "Nie zdefiniowano katalogu dokumentów pracowników w ustawieniach aplikacji."}
                                </Tooltip>
                              }
                            >
                              <span className="d-inline-block">
                                <Button
                                  type="button"
                                  variant="primary"
                                  disabled={!employeeDocumentsRootPath?.trim()}
                                  style={!employeeDocumentsRootPath?.trim() ? {pointerEvents: "none"} : undefined}
                                  onClick={saveContractDocumentToEmployeeFolder}
                                >
                                  Zapisz DOCX w folderze pracownika
                                </Button>
                              </span>
                            </OverlayTrigger>
                          )}
                        </div>
                      </div>
                      {savedEmployeeDocumentPath && (
                        <div className="col-12">
                          <div className="alert alert-success py-2 mb-0 text-break">
                            Zapisano: <strong>{savedEmployeeDocumentPath}</strong>
                          </div>
                        </div>
                      )}
                      {contractDocumentPreview && (
                        <div className="col-12">
                          <div className="small text-muted mb-1">Podgląd po podstawieniu danych zapisanej umowy:</div>
                          <div className="contract-document-preview-shell">
                            {isContractRichText(contractDocumentPreview) ? (
                              <div
                                className="contract-document-preview"
                                dangerouslySetInnerHTML={{__html: sanitizeContractRichHtml(unwrapContractRichText(contractDocumentPreview))}}
                              />
                            ) : (
                              <pre className="contract-document-preview contract-document-preview-plain">{contractDocumentPreview}</pre>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="contract-form-section mb-0">
                  <div className="contract-form-section-title">Pozostałe informacje</div>
                  <div className="row g-3">
                    <div className="col-lg-4">
                      <label className="form-label" htmlFor="plannedSeveranceCost">Planowany koszt odprawy</label>
                      <NumberInput propertyName="plannedSeveranceCost" defaultValue="" min="0" max="99999999" required={false} state={state}/>
                    </div>
                    <div className="col-lg-8">
                      <label className="form-label" htmlFor="severanceNotes">Uwagi do odprawy</label>
                      <TextInput propertyName="severanceNotes" required={false} state={state}/>
                    </div>
                    <div className="col-12">
                      <label className="form-label" htmlFor="notes">Uwagi do umowy</label>
                      <TextArea propertyName="notes" required={false} rows="3" state={state}/>
                    </div>
                  </div>
                </div>

                <div className={updateConflict ? "alert alert-danger mt-3 mb-0" : "d-none"}>
                  <FontAwesomeIcon icon={faInfoCircle}/>&nbsp;
                  Nie można zapisać tej umowy, ponieważ istnieje już umowa o tym identyfikatorze.
                </div>
              </form>
            </Modal.Body>
            <Modal.Footer className="contract-form-footer">
              {!currentFormState.formAddingDataMode && (
                <Button variant="outline-danger" onClick={() => setAllowDelete(true)}>Usuń</Button>
              )}
              <div className="ms-auto d-flex gap-2">
                <Button variant="secondary" id="btn-close" onClick={onClose}>Zamknij</Button>
                <Button
                  variant="primary"
                  onClick={onSaveItemClick}
                  disabled={currentFormState.formSaveButtonDisabled || savingContract}
                >
                  {savingContract ? "Zapisywanie..." : "Zapisz"}
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        )}
      </div>

      <DeleteWarningModal state={state} onDelete={onDelete} deleteItemName={itemName}/>
    </div>
  )
}

export default Contract;



